// Scope-aware broadcast queries. Two endpoints, scope-dispatched at call time:
//   • { kind: 'platform' }                              → /admin/broadcasts
//   • { kind: 'group', groupKind, groupId }             → /groups/{id}/broadcasts
//
// The unified `/groups/{id}/broadcasts` URL covers Circles, Networks, and
// Spaces; the server resolves the row's kind before delegating. `groupKind`
// stays on the scope for cache-key tagging but no longer drives the URL.
//
// Response shape is identical across all scopes; the only thing that differs
// is the row set. Filter-tab counts are exposed through the parallel
// `broadcasts/status-counts` endpoint on each path.

import { useQuery } from '@tanstack/react-query';
import type { components } from '@leanin/api-client';
import type { BroadcastStatus } from '@leanin/shared/broadcast-enums';
import { getApiClient } from '@/lib/api-client';
import { throwApiError } from '@/lib/api-error';

export type { BroadcastStatus };

// The ProseMirror JSON body shape used everywhere a broadcast doc crosses
// the wire. The generated OpenAPI types inline this shape into each request
// (no top-level `BroadcastDoc` schema is registered on the server because
// the per-request validators reach into the platform Zod schema directly),
// so we lift the canonical shape off the platform admin create request and
// re-export it as the single web-side reference. Identical shape on the
// group-scope requests; the server validates both against the same Zod doc.
export type BroadcastDoc =
  components['schemas']['AdminCreateBroadcastRequest']['bodyDoc'];

// Re-export the rendered preview triple so mutation hooks and components
// share one canonical name for `{ subject, html, text }`.
export type BroadcastPreview = components['schemas']['BroadcastPreview'];

// Re-export the detail row so update / preview hooks expose one canonical
// response type. Platform + group endpoints both return this shape.
export type AdminBroadcastDetail =
  components['schemas']['AdminBroadcastDetail'];

export type BroadcastGroupKind = 'circle' | 'network' | 'space';

export type BroadcastScope =
  | { readonly kind: 'platform' }
  | {
      readonly kind: 'group';
      readonly groupKind: BroadcastGroupKind;
      readonly groupId: string;
    };

// UI-level filter status (adds an 'all' sentinel for the "no filter" tab).
// The platform admin page and every group's email tab share this vocabulary.
export type BroadcastListStatus = 'all' | 'draft' | 'scheduled' | 'sent';

const BROADCAST_LIST_STATUSES: ReadonlyArray<BroadcastListStatus> = [
  'all',
  'draft',
  'scheduled',
  'sent',
];

export function resolveBroadcastListStatus(
  v: unknown,
): BroadcastListStatus | undefined {
  return typeof v === 'string' &&
    (BROADCAST_LIST_STATUSES as ReadonlyArray<string>).includes(v)
    ? (v as BroadcastListStatus)
    : undefined;
}

export interface BroadcastsPageParams {
  readonly status?: BroadcastStatus;
  readonly cursor?: string;
  readonly pageSize?: number;
}

export const broadcastKeys = {
  all: ['broadcasts'] as const,
  scope: (scope: BroadcastScope) =>
    [...broadcastKeys.all, scopeKey(scope)] as const,
  list: (scope: BroadcastScope, params: BroadcastsPageParams) =>
    [...broadcastKeys.scope(scope), 'list', params] as const,
  statusCounts: (scope: BroadcastScope) =>
    [...broadcastKeys.scope(scope), 'status-counts'] as const,
  detail: (scope: BroadcastScope, id: string) =>
    [...broadcastKeys.scope(scope), 'detail', id] as const,
};

function scopeKey(scope: BroadcastScope): string {
  return scope.kind === 'platform'
    ? 'platform'
    : `${scope.groupKind}:${scope.groupId}`;
}

/**
 * Build a `BroadcastScope` for a group's broadcasts. The canonical place to
 * tag a (kind, groupId) into the discriminated scope union — every consumer
 * (mutation hooks, cache invalidation, preview drivers) routes through this
 * to avoid spelling out the shape inline.
 */
export function groupBroadcastScope(
  kind: BroadcastGroupKind,
  groupId: string,
): BroadcastScope {
  return { kind: 'group', groupKind: kind, groupId };
}

export async function fetchBroadcasts(
  scope: BroadcastScope,
  params: BroadcastsPageParams,
) {
  const client = getApiClient();
  const query = {
    ...(params.cursor !== undefined ? { cursor: params.cursor } : {}),
    ...(params.pageSize !== undefined ? { pageSize: params.pageSize } : {}),
    ...(params.status !== undefined ? { status: params.status } : {}),
  };
  if (scope.kind === 'platform') {
    const { data, error } = await client.GET('/api/v1/admin/broadcasts', {
      params: { query },
    });
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/groups/{id}/broadcasts',
    { params: { path: { id: scope.groupId }, query } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBroadcastStatusCounts(scope: BroadcastScope) {
  const client = getApiClient();
  if (scope.kind === 'platform') {
    const { data, error } = await client.GET(
      '/api/v1/admin/broadcasts/status-counts',
      {},
    );
    if (data === undefined) throwApiError(error);
    return data;
  }
  const { data, error } = await client.GET(
    '/api/v1/groups/{id}/broadcasts/status-counts',
    { params: { path: { id: scope.groupId } } },
  );
  if (data === undefined) throwApiError(error);
  return data;
}

export async function fetchBroadcastDetail(
  scope: BroadcastScope,
  broadcastId: string,
) {
  const client = getApiClient();
  if (scope.kind === 'platform') {
    const { data, error, response } = await client.GET(
      '/api/v1/admin/broadcasts/{id}',
      { params: { path: { id: broadcastId } } },
    );
    if (data === undefined) throwApiError(error, response);
    return data.data;
  }
  const { data, error, response } = await client.GET(
    '/api/v1/groups/{id}/broadcasts/{broadcastId}',
    { params: { path: { id: scope.groupId, broadcastId } } },
  );
  if (data === undefined) throwApiError(error, response);
  return data.data;
}

export function useBroadcasts(
  scope: BroadcastScope,
  params: BroadcastsPageParams,
) {
  return useQuery({
    queryKey: broadcastKeys.list(scope, params),
    queryFn: () => fetchBroadcasts(scope, params),
  });
}

export function useBroadcastStatusCounts(scope: BroadcastScope) {
  return useQuery({
    queryKey: broadcastKeys.statusCounts(scope),
    queryFn: () => fetchBroadcastStatusCounts(scope),
  });
}

export function useBroadcastDetail(scope: BroadcastScope, broadcastId: string) {
  return useQuery({
    queryKey: broadcastKeys.detail(scope, broadcastId),
    queryFn: () => fetchBroadcastDetail(scope, broadcastId),
    enabled: broadcastId.length > 0,
  });
}
